import {Form, Formik, FormikProps} from 'formik';
import {filter, forEach, get, isEmpty, map, mapKeys, omitBy, replace, some} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useMemo, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    ConfirmModal, DateTimePicker, InputFormik, DataWrapper, EditableSessionParticipants, AssignmentSelectorsFormik,
} from 'components';
import {UserContext} from 'context';
import {DateFormat, TimeFormat, ValidationCode} from 'enums';
import {usePrevious, useUserInfoSchema} from 'hooks';
import {AgentParticipant, ClientParticipant, Group, SessionAppointment} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, InviteeParticipantRole, ParticipantRole, UserParticipantRole, UserSummary} from 'types';
import {apiUtils, dataUtils, dateUtils, generateUtils, sessionUtils, userAuthUtils, userInfoUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.APPOINTMENTS.APPOINTMENT_MODAL;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const SESSION_T_KEYS = T_KEYS_COMMON.SESSION;

const ROUND_NEAREST_MINUTES = 5;
const DATE_ERRORS_OVERRIDES = {
    [ValidationCode.MinDate]: T_KEYS.ERROR.INCORRECT_START_DATE,
    [ValidationCode.Duplicated]: T_KEYS.ERROR.INCORRECT_SCHEDULE,
    [ValidationCode.MaxDate]: T_KEYS.ERROR.INCORRECT_END_DATE,
    [ValidationCode.MaxSize]: T_KEYS.ERROR.INCORRECT_DURATION
};

const TEXT_ERRORS = {
    FORBIDDEN_ASSIGNMENT: 'Forbidden assignment for participant',
    INVALID_SESSION_STATE: 'Session is not in "NEW" state'
};

export const DEFAULT_MAX_PARTICIPANTS_BY_ROLE = {
    [ParticipantRole.Agent]: 1,
    [ParticipantRole.Client]: 1,
    [ParticipantRole.Assistant]: 1,
};

type FormTemplateProps = {
    open: boolean;
    title: string;
    loading: boolean;
    error: ApiError;
    timeZone: string;
    dateFormat: DateFormat;
    timeFormat: TimeFormat;
    now: moment.Moment;
    groups: Group[];
    assistantEnabled: boolean;
    guestEnabled: boolean;
    maxAssistants: number;
    maxGuests: number;
    maxParticipants: number;
    users: UserSummary[];
    isEdit: boolean;
    onClose: (confirm: boolean, appointment: SessionAppointment) => void;
    reset: () => void;
} & FormikProps<SessionAppointment>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    open, title, values, initialValues, loading, error, now, timeZone, dateFormat, timeFormat, groups, users, errors, assistantEnabled,
    maxParticipants, guestEnabled, maxGuests, maxAssistants, isEdit,
    setFieldValue, setFieldError, setFieldTouched, submitForm, onClose, reset, resetForm
}: FormTemplateProps) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);
    const showCommonErrorMessage = useMemo(() => error && !apiUtils.isValidationError({response: error.response}), [error]);
    const commonErrorMessage = useMemo(() => {
        const msg = apiUtils.getResponseErrors(error as any);
        if (msg === TEXT_ERRORS.FORBIDDEN_ASSIGNMENT) {
            return t(T_KEYS.ERROR.INCORRECT_ASSIGNMENT);
        } else if (msg === TEXT_ERRORS.INVALID_SESSION_STATE) {
            return t(T_KEYS.ERROR.INVALID_SESSION_STATE);
        }
        return t(T_KEYS_COMMON.MESSAGES.ERROR.UNEXPECTED);
    }, [error, t]);
    const sessionBasePath = useMemo(() => 'session.', []);

    const startMoment = useMemo(() => moment(values.schedule.startDate).tz(timeZone), [values.schedule.startDate, timeZone]);
    const endMoment = useMemo(() => moment(values.schedule.endDate).tz(timeZone), [values.schedule.endDate, timeZone]);
    const isDurationExceeded = useMemo(() => endMoment.isAfter(startMoment.clone().add(1, 'day')), [endMoment, startMoment]);

    const startDateInvalid = useMemo(() =>
        startMoment.isBefore(now.clone().add(-10, 'minute')), [startMoment, now]);
    const startDateErrorMessage = useMemo(() => get(errors, 'schedule.startDate') ||
        apiUtils.getValidationCodeTKey(t, ValidationCode.MinDate, DATE_ERRORS_OVERRIDES), [errors, t]);

    const endDateInvalid = useMemo(() =>
        endMoment.isBefore(startMoment) || isDurationExceeded || endMoment.isAfter(now.clone().add(1, 'year')),
    [endMoment, startMoment, now, isDurationExceeded]);
    const endDateErrorMessage = useMemo(() =>
        get(errors, 'schedule.endDate') ||
        apiUtils.getValidationCodeTKey(t, isDurationExceeded ? ValidationCode.MaxSize : ValidationCode.MaxDate, DATE_ERRORS_OVERRIDES),
    [isDurationExceeded, t, errors]);

    const hasNoChanges = useMemo(() => isEdit && dataUtils.isEqualValues(values, initialValues),
        [isEdit, values, initialValues]);

    const okDisabled = useMemo(() => hasNoChanges || !values.name || isEmpty(values.participants) || endDateInvalid || startDateInvalid ||
        !some(values.participants, {role: ParticipantRole.Client}) ||
        some(values.participants, (p) =>
            p.role === ParticipantRole.Client && !p.data?.userInfo?.id ||
            p.role === ParticipantRole.Agent && !p.data?.eid ||
            p.role === ParticipantRole.Assistant && !p.data?.eid
        ),
    [hasNoChanges, endDateInvalid, startDateInvalid, values]);

    const assignmentSelectionDisabled = useMemo(() =>
        some(values.participants, {role: ParticipantRole.Agent}) &&
        some(values.participants, {role: ParticipantRole.Assistant}), [values.participants]);

    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false, null);
        }
    }, [submitForm, onClose]);

    const handleStartDateUpdate = useCallback((value: moment.Moment) => {
        setFieldValue('schedule.startDate', value);
    }, [setFieldValue]);

    const handleEndDateUpdate = useCallback((value: moment.Moment) => {
        setFieldValue('schedule.endDate', value);
    }, [setFieldValue]);

    const handleAddParticipant = useCallback((role: ParticipantRole) => {
        setFieldValue(`participants[${values.participants.length}]`, {
            role,
            ...(role === ParticipantRole.Client && {controlOnlyInfo: {}}),
            ...(role === ParticipantRole.Guest && {controlOnlyInfo: {}, data: {userInfo: {id: generateUtils.generateClientId()}}})
        });
    }, [setFieldValue, values.participants]);

    const handleRemoveParticipant = useCallback((index: number) => {
        const otherParticipants = filter(values.participants, (_, i) => index !== i);
        setFieldValue('participants', otherParticipants);
    }, [setFieldValue, values.participants]);

    const canRemoveParticipant = useCallback((index: number) => {
        const participant = values.participants[index];
        return participant.role !== ParticipantRole.Client ||
            filter(values.participants, {role: ParticipantRole.Client}).length > 1;
    }, [values.participants]);

    const userInfoErrors = useMemo(() =>
        userInfoUtils.getUserInfoErrors(error, t),
    [error, t]);

    const formErrors = useMemo(() => {
        const fieldsToValidate = ['name', 'schedule.startDate', 'schedule.endDate'];

        forEach(values.participants, (p, i) => {
            if (sessionUtils.isUserParticipant(p)) {
                fieldsToValidate.push(`participants.${i}.data.eid`);
            } else if (sessionUtils.isInviteeParticipant(p)) {
                fieldsToValidate.push(`participants.${i}.data.userInfo.id`);
            }
        });

        const result = apiUtils.getFieldsValidationErrorMessages(
            error, t, map(fieldsToValidate, (field) => ({field: `${sessionBasePath}${field}`, overrides: DATE_ERRORS_OVERRIDES})));

        return mapKeys(omitBy({...result, ...userInfoErrors}, isEmpty), (value, key) => replace(key, sessionBasePath, ''));
    }, [error, sessionBasePath, t, userInfoErrors, values.participants]);

    useEffect(() => {
        forEach(formErrors, (value, key) => {
            setFieldTouched(key);
            setTimeout(() => {
                setFieldError(key, value);
            }, 0);
        });
    }, [t, setFieldError, setFieldTouched, error, formErrors, sessionBasePath]);

    useEffect(() => {
        if (!open && prevOpen) {
            resetForm();
            reset();
        }
    }, [open, prevOpen, reset, resetForm]);

    // TODO: refactor! modal should not live in form template
    return (
        <ConfirmModal
            title={title}
            open={open}
            maxWidth='sm'
            fullWidth
            okDisabled={okDisabled}
            onClose={onModalClose}
        >
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Form>
                    {showCommonErrorMessage && <div className='tw-text-alert tw-mb-8'>{commonErrorMessage}</div>}

                    <div>{t(T_KEYS.LABELS.SESSION_INFO)}</div>
                    <InputFormik name='name' label={t(T_KEYS_COMMON.SESSION.LABELS.NAME)} required />

                    <DateTimePicker
                        timeZone={timeZone}
                        value={startMoment}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                        timeIncrement={ROUND_NEAREST_MINUTES}
                        hasError={startDateInvalid || !!get(errors, 'schedule.startDate')}
                        errorMessage={startDateErrorMessage}
                        label={t(T_KEYS_COMMON.SESSION.LABELS.START_DATE)}
                        className='tw-mt-2'
                        now={now}
                        onChange={handleStartDateUpdate}
                    />

                    <DateTimePicker
                        timeZone={timeZone}
                        value={endMoment}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                        timeIncrement={ROUND_NEAREST_MINUTES}
                        hasError={endDateInvalid || !!get(errors, 'schedule.endDate')}
                        errorMessage={endDateErrorMessage}
                        label={t(T_KEYS_COMMON.SESSION.LABELS.END_DATE)}
                        className='tw-mt-2'
                        now={now}
                        onChange={handleEndDateUpdate}
                    />

                    <div className='tw-mt-8'>{t(SESSION_T_KEYS.LABELS.ASSIGNMENT)}</div>
                    <AssignmentSelectorsFormik
                        name='assignment'
                        grid
                        disabled={assignmentSelectionDisabled}
                        groups={groups}
                    />
                    <div className='tw-mt-1 tw-text-sm tw-text-default-dimmed'>
                        {t(T_KEYS.LABELS.ASSIGNMENT_AGENT_SELECTION)}
                    </div>

                    <div className='tw-mt-8'>
                        <EditableSessionParticipants
                            participants={values.participants}
                            assignment={values.assignment}
                            assistantEnabled={assistantEnabled}
                            guestEnabled={guestEnabled}
                            maxParticipants={maxParticipants}
                            maxParticipantsByRole={{
                                ...DEFAULT_MAX_PARTICIPANTS_BY_ROLE,
                                [ParticipantRole.Assistant]: maxAssistants,
                                [ParticipantRole.Guest]: maxGuests,
                                ...(values.assignment?.groupId ? {[ParticipantRole.Agent]: 0} : {})
                            }}
                            users={users}
                            canRemoveParticipant={canRemoveParticipant}
                            onAddParticipant={handleAddParticipant}
                            onRemoveParticipant={handleRemoveParticipant}
                        />
                    </div>
                </Form>
            </DataWrapper>
        </ConfirmModal>
    );
};

type Props = {
    open: boolean;
    groups: Group[];
    users: UserSummary[];
    session?: SessionAppointment;
    error?: ApiError;
    loading?: boolean;
    reset?: () => void;
    onClose: (confirm: boolean, appointment: SessionAppointment) => void;
};

export const AppointmentModal: React.FC<Props> = ({
    open, groups, users, session, error, loading, reset, onClose
}: Props) => {
    const {t} = useTranslation();
    const {
        settings: {
            common: {timeZone, dateFormat, timeFormat},
            featuresAvailability,
            userSession
        }
    } = useContext(UserContext);
    const [startDate, setStartDate] = useState(moment.tz(timeZone));

    const title = useMemo(() => session ? t(T_KEYS.UPDATE_TITLE) : t(T_KEYS.CREATE_TITLE), [t, session]);

    const {getUserInfoFields, getControlOnlyFields} = useUserInfoSchema({fetchOnInit: false});

    const [userInfoFields, controlOnlyFields] = useMemo(() => [
        getUserInfoFields(session?.assignment),
        getControlOnlyFields(session?.assignment)
    ], [getUserInfoFields, getControlOnlyFields, session?.assignment]);

    const {getUserInfoFields: getGuestInfoFields, getControlOnlyFields: getGuestControlOnlyFields} = useUserInfoSchema({
        fetchOnInit: false,
        role: InviteeParticipantRole.Guest,
        guestEnabled: featuresAvailability.guestEnabled
    });

    const [guestUserInfoFields, guestControlOnlyFields] = useMemo(() => [
        getGuestInfoFields(session?.assignment),
        getGuestControlOnlyFields(session?.assignment)
    ], [getGuestControlOnlyFields, getGuestInfoFields, session?.assignment]);

    const initialValues: SessionAppointment = useMemo(() => session || (
        {
            name: '',
            schedule: {
                startDate: startDate.toISOString(),
                endDate: startDate.clone().add(1, 'hour').toISOString()
            },
            assignment: null,
            participants: [
                ...(userAuthUtils.getLocalUser().inSessionRoles?.includes(UserParticipantRole.Agent) ?
                    [{role: ParticipantRole.Agent, data: {eid: userAuthUtils.getLocalUser().eid}} as AgentParticipant] : []
                ),
                {
                    role: ParticipantRole.Client,
                    data: {
                        userInfo: userInfoUtils.generateEmpty()
                    }
                } as ClientParticipant,
            ]
        }), [session, startDate]);

    const saveAppointment = useCallback((formValues: SessionAppointment) => {
        forEach(formValues.participants, (participant) => {
            if (participant.role === ParticipantRole.Client) {
                participant.data.userInfo = userInfoUtils.format(participant.data?.userInfo, {
                    userInfoFields, controlOnlyFields
                });
            } else if (participant.role === ParticipantRole.Guest) {
                participant.data.userInfo = userInfoUtils.format(participant.data?.userInfo, {
                    userInfoFields: guestUserInfoFields, controlOnlyFields: guestControlOnlyFields
                });
            }
        });
        if (!featuresAvailability.assistantEnabled) {
            formValues.participants = filter(formValues.participants, (participant) => participant.role !== ParticipantRole.Assistant);
        }
        if (!featuresAvailability.guestEnabled) {
            formValues.participants = filter(formValues.participants, (participant) => participant.role !== ParticipantRole.Guest);
        }
        onClose(true, formValues);
    }, [
        featuresAvailability.assistantEnabled, featuresAvailability.guestEnabled, onClose,
        userInfoFields, controlOnlyFields, guestUserInfoFields, guestControlOnlyFields
    ]);

    useEffect(() => {
        if (open) {
            setStartDate(dateUtils.getNextMinutesDate(timeZone, ROUND_NEAREST_MINUTES));
        }
    }, [open, timeZone]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={saveAppointment}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    open={open}
                    title={title}
                    error={error}
                    loading={loading}
                    timeZone={timeZone}
                    dateFormat={dateFormat}
                    timeFormat={timeFormat}
                    maxAssistants={userSession.maxAssistants}
                    assistantEnabled={featuresAvailability.assistantEnabled}
                    guestEnabled={featuresAvailability.guestEnabled}
                    maxGuests={userSession.maxGuests}
                    maxParticipants={userSession.maxParticipants}
                    isEdit={!!session}
                    now={startDate}
                    groups={groups}
                    users={users}
                    reset={reset}
                    onClose={onClose}
                />
            )}
        </Formik>
    );
};